/**
 * 구간과 에피소드의 요약 통계를 채널 카드와 범위 막대로 바꾸는 순수 모델.
 *
 * Owns:
 * - `TimelineEvent`(채널 x 지표)를 그룹이 붙은 채널 카드 목록으로 바꾼다.
 * - **지표별 공유 축**. 같은 지표를 낸 채널이 모두 한 축 위에 있어야 막대 길이를 비교할 수 있다.
 * - 값을 0~1 위치로 바꾼다.
 *
 * Does not own:
 * - 지표와 통계의 표시 이름·설명(`./motionTerms`), 채널 제목과 그룹 판정(`../parquetChannels`).
 * - 아티팩트 파싱(`./timelineArtifact`)과 화면 마크업(`./MotionSummary`).
 *
 * **축 바닥은 0 이다.** 값이 음수인 지표만 축이 자기 최솟값까지 내려간다.
 * 속력과 가속도 크기에서 0 은 "멈춤"이다. 축 바닥을 최솟값까지 올리면 멈춰 있었다는 사실이
 * 막대에서 사라진다. 시간별 그래프도 같은 이유로 바닥을 0 에 둔다.
 */

import {
  renderGroup,
  type ChannelStructureIndex,
  type ParquetChannelGroupId,
} from "../parquetChannels";
import type { TimelineEvent, TimelineMetric } from "./timelineArtifact";

/** 한 지표의 공유 축. `max === min` 인 상수 지표도 그린다(막대가 한 점이 된다). */
export type MotionSummaryScale = {
  min: number;
  max: number;
};

/** 카드 안의 지표 한 줄. 축이 없으면(통계 전부 null) 막대를 그리지 않는다. */
export type MotionSummaryRow = {
  key: string;
  metric: TimelineMetric;
  scale: MotionSummaryScale | null;
};

/** 채널 카드 하나. `unavailable` 이면 `rows` 가 비어 있고 사유만 남는다. */
export type MotionSummaryCard = {
  channel: string;
  group: ParquetChannelGroupId;
  rows: readonly MotionSummaryRow[];
  unavailable: string | null;
  /** 카드 머리에 표시할 대표 줄. 속력이 있으면 속력, 없으면 p95 를 낸 첫 지표다. */
  headline: MotionSummaryRow | null;
};

// 대표 지표. 사람이 "이 채널이 얼마나 빨랐나"를 알려고 먼저 읽는 값이다.
const HEADLINE_METRIC = "speed";

function finite(value: number | null | undefined): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

/** 이 지표 통계에서 축이 포함해야 하는 값들. 없는 통계는 건너뛴다. */
function metricExtents(metric: TimelineMetric): readonly number[] {
  return [
    finite(metric.min?.value),
    finite(metric.max?.value),
    finite(metric.mean),
    finite(metric.p95),
    finite(metric.rms),
  ].filter((value): value is number => value !== null);
}

/**
 * 지표별 공유 축. 격자에 있는 모든 채널의 통계를 덮는 한 축이다.
 *
 * 채널마다 자기 최댓값으로 정규화하면 모든 막대가 끝까지 찬다. 그러면 오른팔이 왼팔의
 * 6배였다는 실측 차이가 그림에서 사라진다.
 */
export function motionSummaryScales(event: TimelineEvent): ReadonlyMap<string, MotionSummaryScale> {
  const scales = new Map<string, MotionSummaryScale>();
  for (const channel of event.channels.values()) {
    for (const [key, metric] of channel.metrics) {
      const values = metricExtents(metric);
      if (values.length === 0) continue;
      const previous = scales.get(key);
      scales.set(key, {
        min: Math.min(previous?.min ?? 0, 0, ...values),
        max: Math.max(previous?.max ?? 0, 0, ...values),
      });
    }
  }
  return scales;
}

/**
 * 값의 0~1 위치. 축 밖 값은 끝에 고정한다.
 * 축이 이미 모든 값을 덮으므로 이 경우는 보통 일어나지 않는다.
 */
export function motionBarPosition(value: number, scale: MotionSummaryScale): number {
  const span = scale.max - scale.min;
  if (!(span > 0)) return 0;
  return Math.max(0, Math.min(1, (value - scale.min) / span));
}

/** 구간(또는 에피소드 전체) 하나의 채널 카드 목록. 순서는 아티팩트가 준 채널 순서다. */
export function motionSummaryCards(
  event: TimelineEvent,
  channels: ChannelStructureIndex,
): readonly MotionSummaryCard[] {
  const scales = motionSummaryScales(event);
  return [...event.channels].map(([name, channel]) => {
    const rows = [...channel.metrics].map(([key, metric]) => ({
      key,
      metric,
      scale: metricExtents(metric).length === 0 ? null : (scales.get(key) ?? null),
    }));
    return {
      channel: name,
      group: renderGroup(channels.get(name), name),
      rows,
      unavailable: channel.unavailable,
      headline:
        rows.find((row) => row.key === HEADLINE_METRIC && finite(row.metric.p95) !== null) ??
        rows.find((row) => finite(row.metric.p95) !== null) ??
        null,
    };
  });
}
